import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable, OnDestroy, Optional, SkipSelf } from '@angular/core';
import { ArrHlp } from 'src/lib/fliko/helpers/array.helper';
import { HashCodeBuilder } from 'src/lib/fliko/helpers/hashCodeBuilder';
import { MapHlp } from 'src/lib/fliko/helpers/map.helper';
import { ToString } from 'src/lib/fliko/helpers/toString.helper';
import { EstateData } from 'src/lib/fliko/models/estate/estate.data';
import { ResidentData } from 'src/lib/fliko/models/person/resident.data';
import { ResolutionStatus } from 'src/lib/fliko/models/resolutions/resolution-enum';
import { ResolutionData, ResolutionDetailsData, ResolutionVoteData } from 'src/lib/fliko/models/resolutions/resolution.data';
import { RentalUnit, RentalUnitType } from 'src/lib/fliko/models/units/unit.data';
import { ShareData } from 'src/lib/fliko/models/users/share.data';
import { Nullable } from 'src/lib/fliko/types/typeish.type';
import { RentalUnitTableVM } from 'src/app/shared/components/clickable-row-table/models/value-table.model';
import { AttHlp } from 'src/app/shared/helpers/attachment.helper';
import { LocalizationHelper } from 'src/app/shared/helpers/localization.helper';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { ResidentService } from 'src/app/view/users/services/resident.service';
import { ShareService } from 'src/app/view/users/services/share.service';
import { UnitService } from 'src/app/view/users/services/unit.service';
import {
  ResolutionVM,
  ResResidentVM,
  VotingData,
  ProvisionalVoteVM,
  VoteVM,
  ValidVoteVM,
  ResidentsResolutionsVoteCollection
} from './models/resolution-view-models';
import { ResolutionVoteEx } from './models/voteTable-view-models';
import { ResolutionService } from './resolution.service';

interface ProcessData {
  resolutions: ResolutionData[];
  residents: ResidentData[];
  shares: ShareData[];
  resolutionsMap: Map<string, ResolutionData>;
  residentsMap: Map<string, ResidentData>;
  sharesMap: Map<string, ShareData>;
  unitsMap: Map<string, RentalUnit>;
  estate: EstateData;
}

interface VotesProcessData extends ProcessData {
  votes: ResolutionVoteData[];
}

interface ResidentsProcessData extends ProcessData {
  votes: ResolutionVoteData[];
}

@Injectable({
  providedIn: 'root'
})
export class ResolutionDataService implements OnDestroy {
  private commonData$ = new BehaviorSubject<ProcessData | null>(null);
  private resolutions$ = new BehaviorSubject<ResolutionVM[]>([]);
  private residents$ = new BehaviorSubject<ResResidentVM[]>([]);

  private readonly locHelper = new LocalizationHelper(this.ls);

  private subscription$ = new Subscription();

  constructor(
    private resolutionService: ResolutionService,
    private residentService: ResidentService,
    private unitService: UnitService,
    private shareService: ShareService,
    private ls: LocalizationService,
    private ctxDS: ContextDataService,
    @Optional() @SkipSelf() thisService?: ResolutionDataService
  ) {
    if (thisService) throw new Error('Multiple instances of ResolutionDataService');

    // TODO: Check resolutions with tenants
    this.subscription$.add(
      combineLatest([
        this.resolutionService.getResolutions$(),
        this.residentService.getResidents$(),
        this.shareService.getShares$(),
        this.unitService.getRentalUnitsMap$(),
        this.ctxDS.currentEstate$
      ]).subscribe(([resolutions, residents, shares, unitsMap, estate]) => {
        if (estate) {
          const resolutionsMap = MapHlp.idMap(resolutions);
          const residentsMap = MapHlp.idMap(residents);
          const sharesMap = MapHlp.idMap(shares);
          this.commonData$.next({ resolutions, residents, shares, resolutionsMap, residentsMap, sharesMap, unitsMap, estate });
        } else {
          this.commonData$.next(null);
        }
      })
    );

    this.subscription$.add(
      combineLatest([this.commonData$, this.resolutionService.getDetails$()]).subscribe(([common, details]) => {
        const sanitizedDetails = details.map((d) => {
          const attachments = d?.attachments
            ? (d.attachments as (string | { name: string; url: string })[]).map((att) =>
                typeof att === 'object' && att != null && 'url' in att ? att.url : att
              )
            : null;
          return { ...d, attachments };
        });
        this.resolutions$.next(common ? this.createResolutionVMs(common.resolutions, MapHlp.idMap(sanitizedDetails)) : []);
      })
    );

    this.subscription$.add(
      combineLatest([this.commonData$, this.resolutionService.getAllVotes$()]).subscribe(([common, votes]) =>
        this.residents$.next(common ? this.createResidentVMs({ votes, ...common }) : [])
      )
    );
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  public getAllResolutions$(): BehaviorSubject<ResolutionVM[]> {
    return this.resolutions$;
  }

  public getResolution$(resolutionId: string): Observable<ResolutionVM | undefined> {
    return this.resolutions$.pipe(map((resolutions) => resolutions.find((res) => res.resolution.id === resolutionId)));
  }

  public getResidents$(): Observable<ResResidentVM[]> {
    return this.residents$;
  }

  public getResolutionVotingData$(resolutionId: string): Observable<VotingData> {
    return combineLatest([
      this.commonData$,
      this.resolutionService.getResolution$(resolutionId),
      this.resolutionService.getVotes$(resolutionId)
    ]).pipe(
      map(([common, resolution, votes]) => {
        if (common && resolution) {
          const data = { ...common, votes };
          const unvotedShares = data.shares.filter((s) => !votes.find((v) => v.apartmentShareId == s.id));
          return { voted: this.createValidVoteVMs(data), possible: this.createPossibleVoteVMs(data, unvotedShares, resolution) };
        } else {
          return { voted: [], possible: [] };
        }
      })
    );
  }

  public getResidentVotingData$(residentId: string, onlyActive: boolean = true): Observable<VotingData> {
    const addVotes = <T extends VoteVM>(from: T[], to: T[]) => {
      const votes = onlyActive ? from.filter((x) => x.resolution?.status == ResolutionStatus.ACTIVE) : from;
      to.push(...votes);
    };
    return this.residents$.pipe(
      map((residents) => {
        const resident = residents.find((x) => x.resident.id == residentId);
        const voted: ValidVoteVM[] = [];
        const possible: ProvisionalVoteVM[] = [];
        if (resident) {
          resident.votes.forEach((resolutionVotes) => {
            addVotes(resolutionVotes.voted, voted);
            addVotes(resolutionVotes.blocked, voted);
            addVotes(resolutionVotes.possible, possible);
          });
        }
        return { voted, possible };
      })
    );
  }

  public groupResidentsVotes<T>(data: VoteVM[], target: Map<string, T[]>, process: (x: VoteVM, ownerIndex: 0 | 1) => T): void {
    data.forEach((vote) => {
      if (vote.owners?.length) {
        MapHlp.arrayMapAdd(target, vote.owners[0].id, process(vote, 0));
        const coOwnerId = vote.owners[1]?.id;
        if (coOwnerId) MapHlp.arrayMapAdd(target, coOwnerId, process(vote, 1));
      }
    });
  }

  public groupResolutionsVotes<T>(data: VoteVM[], target: Map<string, T[]>, process: (x: VoteVM) => T): void {
    data.forEach((vote) => MapHlp.arrayMapAdd(target, vote.resolution?.id, process(vote)));
  }

  private createValidVoteVMs(data: VotesProcessData): ValidVoteVM[] {
    return data.votes.map((vote) => {
      const voter = data.residentsMap.get(vote.userId);
      const share = data.sharesMap.get(vote.apartmentShareId);
      const rentalUnit = data.unitsMap.get(share?.unitId ?? '');
      const rentalUnitType = share?.unitType ?? RentalUnitType.APARTMENT;
      const owners = share ? this.getOwners(share, data.residentsMap) : voter ? [voter] : [];

      const resolution = data.resolutionsMap.get(vote.resolutionId);
      const unitNumber = vote.unitNumber ?? ToString.shortNumber(rentalUnit);
      const shareCount = vote.shareCount ?? 0;
      const sharesPercentage = (shareCount / data.estate.totalShares) * 100;

      const hash = new HashCodeBuilder(share?.id ?? vote.id, resolution?.id).valueOf();

      // prettier-ignore
      return { hash, data: vote, resolution, voter, owners, share,
               rentalUnit, rentalUnitType, unitNumber, shareCount, sharesPercentage };
    });
  }

  private createPossibleVoteVMs(data: VotesProcessData, shares: ShareData[], resolution: ResolutionData): ProvisionalVoteVM[] {
    return ArrHlp.filterMap(
      shares,
      (share) => !!share.ownerId,
      (share) => {
        const voter = undefined;
        const rentalUnit = data.unitsMap.get(share?.unitId ?? '');
        const rentalUnitType = share?.unitType ?? RentalUnitType.APARTMENT;
        const owners = this.getOwners(share, data.residentsMap);

        const unitNumber = ToString.shortNumber(rentalUnit);
        const shareCount = share.shareCount;
        const sharesPercentage = (shareCount / data.estate.totalShares) * 100;

        const hash = new HashCodeBuilder(share.id, resolution.id).valueOf();
        // prettier-ignore
        return { hash, data: undefined, voter, owners, share, resolution,
                 rentalUnit, rentalUnitType, unitNumber, shareCount, sharesPercentage };
      }
    );
  }

  private getOwners(share: ShareData, residents: Map<string, ResidentData>): ResidentData[] {
    const owners: ResidentData[] = [];

    const owner1 = residents.get(share.ownerId);
    const owner2 = share.coOwnerId ? residents.get(share.coOwnerId) : null;

    if (owner1) owners.push(owner1);
    if (owner2) owners.push(owner2);

    return owners;
  }

  private createResidentVMs(data: ResidentsProcessData): ResResidentVM[] {
    const ret: ResResidentVM[] = [];

    const sharesByResident = MapHlp.idMapArray(data.shares, ['ownerId', 'coOwnerId']);
    const votesMap = new Map<string, Map<string, ValidVoteVM[]>>(); // ? key1 - resolutionId, key2 - residentId

    for (const vote of this.createValidVoteVMs(data)) {
      const resolutionVoteMap = MapHlp.getSet(votesMap, vote.data.resolutionId, new Map<string, ValidVoteVM[]>());

      if (vote.owners[0]) MapHlp.arrayMapAdd(resolutionVoteMap, vote.owners[0].id, vote);
      if (vote.owners[1]) MapHlp.arrayMapAdd(resolutionVoteMap, vote.owners[1].id, vote);
    }

    data.residents.forEach((resident) => {
      const shares = sharesByResident.get(resident.id) ?? [];
      const resolutions = {
        yes: new Set<ResolutionData>(),
        no: new Set<ResolutionData>(),
        hold: new Set<ResolutionData>(),
        mixed: new Set<ResolutionData>(),
        none: new Set<ResolutionData>()
      };
      const votes = new Map<string, ResidentsResolutionsVoteCollection>();
      let voteCounter = 0;

      data.resolutions.forEach((resolution) => {
        const resVotes = votesMap.get(resolution.id)?.get(resident.id) ?? [];
        let voteValue: ResolutionVoteEx = resVotes[0]?.data.vote ?? 'none';

        const voted: ValidVoteVM[] = [];
        const blocked: ValidVoteVM[] = [];

        voteCounter += resVotes.length;
        resVotes.forEach((vote) => {
          (vote.data.userId == resident.id ? voted : blocked).push(vote);

          if (voteValue != vote.data.vote) voteValue = 'mixed';
        });

        const sharesWithoutVote = shares.filter((s) => !resVotes.find((v) => s.id == v.data.apartmentShareId));

        // HACK: typescript looses type info because of reasons
        if (sharesWithoutVote.length && (voteValue as ResolutionVoteEx) != 'none') voteValue = 'mixed';

        const possible = this.createPossibleVoteVMs(data, sharesWithoutVote, resolution);

        resolutions[voteValue].add(resolution);
        votes.set(resolution.id, { voted, blocked, possible });
      });

      // ? skip residents without shares and votes
      if (shares.length || voteCounter) {
        let shareSum = 0;
        const units: RentalUnitTableVM[] = [];

        shares.forEach((share) => {
          shareSum += share.shareCount;
          const unit = data.unitsMap.get(share.unitId);
          if (unit) units.push({ unit, type: share.unitType });
        });

        const share = this.locHelper.shareOfTotal(shareSum, this.ctxDS.peekCurrentEstate()!.totalShares);

        ret.push({ resident, shares, share, units, votes, resolutions });
      }
    });

    return ret;
  }

  private createResolutionVMs(resolutions: ResolutionData[], detailsMap: Map<string, ResolutionDetailsData>): ResolutionVM[] {
    return resolutions.map((resolution) => {
      const details = detailsMap.get(resolution.id) ?? {
        id: resolution.id,
        estateId: '',
        content: this.ls.translate('label.noData')
      };
      return {
        resolution,
        details,
        closeDate: resolution.voteEnd || resolution.voteTerm || 0,
        attachments: AttHlp.toFileInStorageVM(details.attachments),
        photos: AttHlp.toFileInStorageVM(this.getPhotoPaths(details.photos))
      };
    });
  }

  private getPhotoPaths(photos: Nullable<string[]>): string[] {
    return (photos ?? []).filter((x): x is string => !!x);
  }
}
